import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FaDumbbell, FaAppleAlt, FaVideo, FaBook, FaCalendar, FaSignOutAlt, FaWhatsapp, FaFire, FaStar, FaCrown, FaCog, FaChartLine, FaClipboardList, FaClock, FaCheckCircle, FaUtensils, FaCreditCard, FaShoppingCart, FaBell, FaTrophy } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import ClinicalFormWidget from '@/components/ClinicalFormWidget';
import { useGender } from '@/context/GenderContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { VIPPlanExpiredModal, VIPSpecialPriceExpiredModal } from '@/components/VIPModals';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import { VIPRenewalModal } from '@/components/VIPSubscription';
import MissingDataModal from '@/components/MissingDataModal';
import MonthlyUpdateSection from '@/components/MonthlyUpdateSection';
import PlanExpiredOverlay from '@/components/PlanExpiredOverlay';
import FollowupModal from '@/components/FollowupModal';
import CoachMessageBadge from '@/components/CoachMessageBadge';
import CoachMessagesInbox from '@/components/CoachMessagesInbox';
import FavoriteRecipesWidget from '@/components/FavoriteRecipesWidget';
import HormonalCycleBanner from '@/components/HormonalCycleBanner';
import HormonalCycleCard from '@/components/HormonalCycleCard';
import HormonalCyclePhaseModal from '@/components/HormonalCyclePhaseModal';
import FitEvolutionUpdateBanner from '@/components/FitEvolutionUpdateBanner';
import FitEvolutionPendingReleaseBadge from '@/components/FitEvolutionPendingReleaseBadge';
import CoachProgressCommentBanner from '@/components/CoachProgressCommentBanner';
import FitEvolutionWorkoutProgressWidget from '@/components/FitEvolutionWorkoutProgressWidget';
import WelcomeOnboarding from '@/components/WelcomeOnboarding';

// 2026-02 · Banner de comunidad Fit Evolution (visible SÓLO para clientas
// con plan Fit Evolution — `subscription_plan === 'basic'` — y sólo cuando
// el admin activó al menos un canal desde Suplementos → Comunidad).
function FitEvolutionCommunityBanner({ user }) {
  const [cfg, setCfg] = useState(null);
  // 2026-02 · restringir a plan Fit Evolution. Otras suscripciones NO deben
  // ver este banner aunque el admin tenga canales activos.
  const isFitEvolutionUser = (user?.subscription_plan || '').toLowerCase() === 'basic';
  useEffect(() => {
    if (!isFitEvolutionUser) { setCfg(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/personalized-plans/fit-evolution/community-config`);
        if (alive) setCfg(res.data || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user, isFitEvolutionUser]);
  if (!isFitEvolutionUser) return null;
  if (!cfg) return null;
  const waOn = !!cfg?.whatsapp?.enabled;
  const tgOn = !!cfg?.telegram?.enabled;
  if (!waOn && !tgOn) return null;
  return (
    <div
      className="mb-4 rounded-2xl p-5 md:p-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(236,72,153,0.14), rgba(34,197,94,0.10) 60%, rgba(59,130,246,0.10))',
        border: '1px solid rgba(236,72,153,0.35)',
        boxShadow: '0 10px 30px -12px rgba(236,72,153,0.25)',
      }}
      data-testid="fit-evo-community-banner"
    >
      <p
        className="text-sm md:text-base font-black tracking-widest mb-2"
        style={{ color: '#f9a8d4', fontFamily: 'Poppins, sans-serif' }}
      >
        ESTOY MÁS CERCA DE TI 💗
      </p>
      <p className="text-white/90 text-sm md:text-base mb-2 leading-snug">
        Únete a la comunidad de Fit Evolution y recibe directamente de mí:
      </p>
      <ul className="text-white/85 text-sm leading-relaxed mb-2 space-y-0.5">
        <li>✨ Tips y material de apoyo</li>
        <li>✨ Recordatorios importantes</li>
        <li>✨ Resolución de dudas</li>
        <li>✨ Seguimiento y acompañamiento de tu proceso</li>
      </ul>
      <p className="text-white/70 text-xs md:text-sm italic mb-4 leading-snug">
        Un espacio creado para ayudarte a avanzar con mayor claridad y aprovechar al máximo tu proceso.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        {waOn && (
          <a
            href={cfg.whatsapp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs md:text-sm font-black text-white uppercase tracking-wider transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
            data-testid="fit-evo-community-btn-whatsapp"
          >
            💬 UNIRME A LA COMUNIDAD DE WHATSAPP
          </a>
        )}
        {tgOn && (
          <a
            href={cfg.telegram.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs md:text-sm font-black text-white uppercase tracking-wider transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #2AABEE, #229ED9)' }}
            data-testid="fit-evo-community-btn-telegram"
          >
            ✈️ UNIRME A LA COMUNIDAD DE TELEGRAM
          </a>
        )}
      </div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para convertir URLs relativas a absolutas
const getAbsoluteImageUrl = (url) => {
  if (!url || url === '' || url === 'null' || url === 'undefined') return null;
  // Si ya es una URL absoluta, devolverla tal cual
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Pero si es de un dominio antiguo, reemplazar
    const oldDomains = ['workout-renewal.preview.emergentagent.com', 'fitness-backend.preview.emergentagent.com'];
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        return url.replace(`https://${oldDomain}`, BACKEND_URL).replace(`http://${oldDomain}`, BACKEND_URL);
      }
    }
    return url;
  }
  // Si es relativa, construir la URL absoluta
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Mapeo de iconos de string a componentes
const ICON_MAP = {
  FaFire, FaDumbbell, FaAppleAlt, FaVideo, FaBook, FaCalendar, FaChartLine
};

// Planes que requieren plan personalizado (NO crean su propia dieta)
const PERSONALIZED_PLANS = ['premium', 'gold', 'competitivo', 'competitive', 'vip', 'competencia', 'gold vip'];

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { isMale, getText, theme } = useGender();
  const { config: siteConfig } = useSiteConfig();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platformModules, setPlatformModules] = useState([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [planInfo, setPlanInfo] = useState(null); // Información del plan (precio, etc.)
  
  // Estados para modales VIP
  const [showVIPExpiredModal, setShowVIPExpiredModal] = useState(false);
  const [showSpecialPriceExpiredModal, setShowSpecialPriceExpiredModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [vipStatus, setVipStatus] = useState(null);
  
  // Estado para modal de perfil
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileGender, setProfileGender] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Estado para plan personalizado
  const [personalizedPlanStatus, setPersonalizedPlanStatus] = useState(null);
  const [personalizedPlanLoading, setPersonalizedPlanLoading] = useState(true); // Track loading state
  // 2026-02 · Composición Corporal para usuarios con plan personalizado (Premium/Competitivo/VIP GOL)
  const [bodyComposition, setBodyComposition] = useState(null);
  
  // Estado para actualizaciones pendientes del formulario
  const [formUpdateStatus, setFormUpdateStatus] = useState(null);
  const [showFormUpdateModal, setShowFormUpdateModal] = useState(false);
  
  // Estado para planes Basic (rutina/dieta)
  const [basicPlanStatus, setBasicPlanStatus] = useState(null);
  
  // Estado para imágenes dinámicas de contenido
  const [dynamicImages, setDynamicImages] = useState({
    ebooks: null,
    videos: null
  });

  // Estado para permisos del plan del usuario
  const [planPermissions, setPlanPermissions] = useState(null);
  
  // Estado para secciones en construcción
  const [sectionsUnderConstruction, setSectionsUnderConstruction] = useState({});
  
  // Estado para followup de patologías
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  
  // Estado para trackear imágenes cargadas (evita "flash" de imagen vacía)
  const [loadedImages, setLoadedImages] = useState({});

  // Usar subscription_plan del user prop (viene del login)
  // Normalización defensiva del plan del usuario: recorta prefijos residuales
  // ("renewal_", "vip-renewal-", "plan-"…) y aliases para que "renewal_premium"
  // se comporte como "premium" — evita que los programas queden vacíos post-renovación.
  const userPlan = (() => {
    const raw = (user?.subscription_plan || '').toString().toLowerCase().trim();
    if (!raw) return 'free';
    const clean = raw
      .replace(/^(vip-renewal-|vip_renewal_|renewal-|renewal_|renovación |renovacion |plan-|plan_|plan )/i, '')
      .replace(/^básico$/i, 'basic').replace(/^basico$/i, 'basic')
      .replace(/^competitiva$/i, 'competitivo');
    return clean || 'free';
  })();
  const userRole = user?.role;
  // Considerar como plan pago cualquier plan que no sea 'free', vacío o nulo
  const hasPaidPlan = userPlan && userPlan !== 'free' && userPlan !== '';
  
  // Verificar si necesita plan personalizado (más flexible)
  const needsPersonalizedPlan = PERSONALIZED_PLANS.some(plan => 
    userPlan === plan || 
    userPlan.includes(plan) || 
    plan.includes(userPlan) ||
    userPlan.replace(/\s+/g, '') === plan.replace(/\s+/g, '')
  );
  
  // Detectar plan basic (Fit Evolution) — incluye variantes de nombre display renombradas
  // por la admin (ej: "fit evolution", "fit-evolution", "fitevolution").
  const isBasicUser = (() => {
    if (!userPlan) return false;
    const norm = userPlan.toLowerCase().trim().replace(/[\s_-]+/g, '');
    return (
      norm === 'basic' ||
      norm === 'planbasic' ||
      norm === 'básico' ||
      norm === 'basico' ||
      norm === 'planbasico' ||
      norm === 'planbásico' ||
      norm === 'fitevolution' ||
      norm === 'planfitevolution' ||
      norm.includes('basic') ||
      norm.includes('básico') ||
      norm.includes('basico') ||
      norm.includes('fitevolution')
    );
  })();

  // Cargar imágenes dinámicas de ebooks/videos
  const fetchDynamicImages = useCallback(async () => {
    try {
      // Obtener primer ebook para su portada
      const ebooksRes = await axios.get(`${API}/ebooks?limit=1`);
      const firstEbook = ebooksRes.data?.[0];
      
      setDynamicImages(prev => ({
        ...prev,
        ebooks: firstEbook?.thumbnail_url || firstEbook?.cover_image || null
      }));
    } catch (error) {
      console.error('Error fetching dynamic images:', error);
    }
  }, []);

  // Cargar permisos del plan del usuario
  const fetchPlanPermissions = useCallback(async () => {
    if (!userPlan || userPlan === 'free') {
      setPlanPermissions(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/public/subscription-plans`);
      const plans = response.data.plans || [];
      
      // Normalizar el plan del usuario
      const normalizedUserPlan = userPlan.toLowerCase().trim();
      
      // Buscar el plan del usuario - comparación flexible
      const userPlanData = plans.find(p => {
        const planName = (p.name || '').toLowerCase().trim();
        const planSlug = (p.slug || '').toLowerCase().trim();
        
        // Coincidencia exacta o parcial
        return planName === normalizedUserPlan || 
               planSlug === normalizedUserPlan ||
               normalizedUserPlan.includes(planSlug) ||
               planSlug.includes(normalizedUserPlan);
      });
      
      console.log('[Dashboard] User plan:', normalizedUserPlan);
      console.log('[Dashboard] Found plan data:', userPlanData?.name, userPlanData?.permissions);
      
      if (userPlanData?.permissions) {
        console.log('Plan permissions loaded:', userPlanData.permissions);
        setPlanPermissions(userPlanData.permissions);
      } else {
        // PLAN BÁSICO y otros planes sin permisos configurados: dar acceso a servicios por defecto
        // Esto preserva el comportamiento anterior para planes como "basic" que tenían lógica especial
        console.log(`[Dashboard] No permissions found for plan "${normalizedUserPlan}". Using default permissions.`);
        
        // Verificar si es un plan de pago (no free)
        const isPaidPlan = normalizedUserPlan !== 'free' && normalizedUserPlan !== '';
        
        if (isPaidPlan) {
          // Planes de pago sin configuración específica: dar acceso a servicios básicos
          setPlanPermissions({
            videos: true,
            ebooks: true,
            recetarios: true,
            my_diet: true,
            my_workout: true,
            data_progress: true,
            gold_calendar: false, // Solo para Gold
            recipe_ideas: true,
            appointments: false,
            tienda: true
          });
        } else {
          setPlanPermissions(null);
        }
      }
    } catch (error) {
      console.error('Error fetching plan permissions:', error);
    }
  }, [userPlan]);

  // Cargar secciones en construcción
  const fetchSectionsUnderConstruction = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/sections-status`);
      setSectionsUnderConstruction(response.data || {});
    } catch (error) {
      console.error('Error fetching sections status:', error);
      setSectionsUnderConstruction({});
    }
  }, []);

  // Verificar estado del plan Basic (rutina/dieta)
  const checkBasicPlanStatus = useCallback(async () => {
    if (!isBasicUser) return;
    
    try {
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.get(`${API}/basic-plan-status`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setBasicPlanStatus(response.data);
    } catch (error) {
      console.error('Error checking basic plan status:', error);
      // Fallback: nunca dejar las tarjetas atascadas en "Cargando…" (path /dashboard)
      setBasicPlanStatus({ diet: {}, workout: {} });
    }
  }, [isBasicUser]);

  // Verificar estado del plan personalizado
  const checkPersonalizedPlanStatus = useCallback(async () => {
    if (!user?.email || !needsPersonalizedPlan) {
      setPersonalizedPlanLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/personalized-plans/check-status/${encodeURIComponent(user.email)}`);
      setPersonalizedPlanStatus(response.data);

      // 2026-02 · Traer body_composition del plan si existe (para tarjeta en Dashboard)
      if (response.data?.has_plan) {
        try {
          const token = localStorage.getItem('deisycfit_token');
          const mp = await axios.get(`${API}/meal-plan/active/by-email/${encodeURIComponent(user.email)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (mp.data?.body_composition) setBodyComposition(mp.data.body_composition);
        } catch (bcErr) {
          console.warn('No se pudo obtener body_composition:', bcErr?.response?.status);
        }
      }
      
      // Verificar si hay actualizaciones pendientes del formulario
      if (response.data?.has_plan) {
        try {
          const updateCheck = await axios.get(`${API}/personalized-plans/form-update-check/${encodeURIComponent(user.email)}?plan_type=${userPlan}`);
          setFormUpdateStatus(updateCheck.data);
          
          // Si hay actualizaciones pendientes, mostrar el aviso
          if (updateCheck.data?.has_pending_update && updateCheck.data?.total_new_questions > 0) {
            // No mostrar modal automáticamente, solo el banner
            console.log(`Hay ${updateCheck.data.total_new_questions} preguntas nuevas en el formulario`);
          }
        } catch (updateError) {
          console.error('Error checking form updates:', updateError);
        }
      }
    } catch (error) {
      console.error('Error checking personalized plan status:', error);
    } finally {
      setPersonalizedPlanLoading(false);
    }
  }, [user?.email, needsPersonalizedPlan, userPlan]);

  // Verificar estado VIP al cargar
  const checkVIPStatus = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      const response = await axios.get(`${API}/vip-status/${encodeURIComponent(user.email)}`);
      const status = response.data;
      setVipStatus(status);
      
      if (status.is_vip) {
        // Verificar si necesita cambiar contraseña (primer login VIP)
        if (status.needs_password_change && !sessionStorage.getItem('password_change_shown')) {
          setShowChangePasswordModal(true);
          sessionStorage.setItem('password_change_shown', 'true');
        }
        // Mostrar modal de expiración si el plan está vencido
        // IMPORTANTE: El modal se muestra SIEMPRE que esté vencido (sin sessionStorage para bloquearlo)
        // Solo se oculta cuando el usuario renueva exitosamente
        else if (status.is_expired && profile?.days_until_expiry <= 0) {
          setShowVIPExpiredModal(true);
        }
        // Verificar si el precio especial expiró (solo si realmente está expirado)
        else if (status.special_price_expired && profile?.days_until_expiry <= 0 && !sessionStorage.getItem('special_price_expired_shown')) {
          setShowSpecialPriceExpiredModal(true);
          sessionStorage.setItem('special_price_expired_shown', 'true');
        }
      }
    } catch (error) {
      console.error('Error checking VIP status:', error);
    }
  }, [user?.email, profile?.days_until_expiry]);

  // Fetch modules from backend (dynamic permissions)
  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modules/active`);
      setPlatformModules(response.data || []);
    } catch (error) {
      console.error('Error fetching platform modules:', error);
      // Fallback to empty array - will use default rendering
      setPlatformModules([]);
    } finally {
      setModulesLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchModules();
    checkVIPStatus();
    checkPersonalizedPlanStatus();
    checkBasicPlanStatus();
    fetchPlanInfo();
    fetchDynamicImages();
    fetchPlanPermissions();
    fetchSectionsUnderConstruction();
    checkFollowupDue();

    // Refresh automático del perfil cuando la alumna vuelve al tab (por ejemplo
    // tras completar un pago en otra ventana). Así el badge cambia de "Plan
    // Vencido" a "Plan Premium" en segundos sin necesidad de cerrar sesión.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchProfile();
        checkVIPStatus();
        checkBasicPlanStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
    
    // Verificar si el usuario necesita cambiar contraseña (primer login)
    if (user?.must_change_password && !sessionStorage.getItem('password_change_shown')) {
      setShowChangePasswordModal(true);
      sessionStorage.setItem('password_change_shown', 'true');
    }
  }, [fetchModules, checkVIPStatus, checkPersonalizedPlanStatus, checkBasicPlanStatus, fetchDynamicImages, fetchPlanPermissions, fetchSectionsUnderConstruction, user?.must_change_password]);

  // Verificar si tiene formulario de seguimiento pendiente
  const checkFollowupDue = async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      if (!token) return;
      const res = await axios.get(`${API}/followup/check`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.data.due && !sessionStorage.getItem('followup_dismissed')) {
        setShowFollowupModal(true);
      }
    } catch (err) {
      console.error('Followup check error:', err);
    }
  };

  // Obtener información del plan actual (precio, etc.)
  const fetchPlanInfo = async () => {
    if (!userPlan || userPlan === 'free') return;
    
    try {
      const response = await axios.get(`${API}/plans`);
      const plans = response.data || [];
      
      // Buscar el plan que coincida con el plan del usuario
      const planIdMap = {
        'basic': 'plan-basic',
        'premium': 'plan-premium',
        'gold': 'plan-gold',
        'lifetime': 'plan-videos'
      };
      
      const planId = planIdMap[userPlan] || `plan-${userPlan}`;
      const currentPlan = plans.find(p => p.id === planId || p.id.includes(userPlan));
      
      if (currentPlan) {
        setPlanInfo(currentPlan);
      }
    } catch (error) {
      console.error('Error fetching plan info:', error);
    }
  };

  const fetchProfile = async () => {
    // Timeout defensivo: si el backend está lento, no dejamos al usuario colgado
    const failsafe = setTimeout(() => setLoading(false), 8000);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.get(`${API}/auth/profile`, { 
        withCredentials: false,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        timeout: 10000,
      });
      setProfile(response.data);
      // Inicializar género del perfil
      if (response.data?.gender) {
        setProfileGender(response.data.gender);
      }
      
      // Log del estado de expiración para debug
      if (response.data?.is_plan_expired) {
        console.log('[Dashboard] Plan expired status:', {
          is_plan_expired: response.data.is_plan_expired,
          days_until_expiry: response.data.days_until_expiry,
          grace_days_remaining: response.data.grace_days_remaining,
          is_fully_expired: response.data.is_fully_expired,
          renewal_price: response.data.renewal_price
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      clearTimeout(failsafe);
      setLoading(false);
    }
  };

  // Guardar género del usuario
  const handleSaveProfile = async () => {
    if (!profileGender) {
      toast.error('Selecciona tu género');
      return;
    }
    
    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, { gender: profileGender }, { withCredentials: false });
      toast.success('Perfil actualizado correctamente');
      setShowProfileModal(false);
      // Actualizar el género en el contexto si existe
      fetchProfile();
    } catch (error) {
      toast.error('Error al actualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: false });
      // Limpiar localStorage
      localStorage.removeItem('deisycfit_user');
      localStorage.removeItem('deisycfit_logged_in');
      window.location.href = '/';
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('¡Hola Deisy! Tengo una consulta sobre mi plan.');
    window.open(`https://wa.me/573195825712?text=${message}`, '_blank');
  };

  // Verificar si una sección está en construcción
  const isSectionUnderConstruction = useCallback((sectionKey) => {
    return sectionsUnderConstruction[sectionKey] === true;
  }, [sectionsUnderConstruction]);

  // Verificar si el plan tiene permiso para un servicio específico
  // SIMPLIFICADO: hasPlanPermission siempre retorna true
  // Ya no usamos permisos del admin, solo las listas allowedPlans
  const hasPlanPermission = useCallback((serviceKey) => {
    return true; // Siempre permitido - controlamos con allowedPlans
  }, []);

  // Verificar si tiene acceso a una función específica
  const hasAccess = useCallback((allowedPlans, serviceKey = null) => {
    // Admin siempre tiene acceso total
    if (userRole === 'admin') return true;
    
    // Si no hay allowedPlans definidos, denegar por defecto (seguridad)
    if (!allowedPlans || allowedPlans.length === 0) return false;
    
    // Ya NO verificamos serviceKey con hasPlanPermission
    // Solo usamos allowedPlans
    
    // Free plans solo tienen acceso si explícitamente permitido
    if (allowedPlans.includes('free')) return true;
    if (userPlan === 'free' || !userPlan) return false;
    
    // Normalizar el plan del usuario
    const normalizedUserPlan = userPlan.toLowerCase().trim().replace(/\s+/g, '_');
    
    // Verificación estricta: comparar con los planes permitidos
    const hasMatchingPlan = allowedPlans.some(allowed => {
      const normalizedAllowed = allowed.toLowerCase().trim().replace(/\s+/g, '_');
      
      // Coincidencia exacta
      if (normalizedUserPlan === normalizedAllowed) return true;
      
      // El plan del usuario contiene el nombre del plan permitido
      // Ej: "alimentación premium" contiene "premium"
      if (normalizedUserPlan.includes(normalizedAllowed)) return true;
      
      // El plan permitido contiene el nombre del plan del usuario
      if (normalizedAllowed.includes(normalizedUserPlan)) return true;
      
      // Comparación sin guiones/underscores
      const cleanUser = normalizedUserPlan.replace(/[-_]/g, '');
      const cleanAllowed = normalizedAllowed.replace(/[-_]/g, '');
      if (cleanUser === cleanAllowed || cleanUser.includes(cleanAllowed) || cleanAllowed.includes(cleanUser)) return true;
      
      // Verificar si coincide con ID del plan (ej: plan_basic vs basic)
      if (normalizedAllowed.includes('plan_')) {
        const planNameFromId = normalizedAllowed.replace('plan_', '');
        if (normalizedUserPlan.includes(planNameFromId)) return true;
      }
      
      return false;
    });
    
    return hasMatchingPlan;
  }, [userPlan, userRole, hasPlanPermission]);

  // Función para navegar o mostrar mensaje de bloqueo
  const handleFeatureClick = (path, allowedPlans, serviceKey = null) => {
    // Verificar si está en construcción
    if (serviceKey && isSectionUnderConstruction(serviceKey)) {
      toast.info('Esta sección está en construcción. ¡Pronto estará disponible!');
      return;
    }
    
    // Ya no verificamos hasPlanPermission - solo usamos hasAccess con allowedPlans
    if (!hasAccess(allowedPlans, serviceKey)) {
      const planNeeded = allowedPlans.includes('basic') ? 'Fit Evolution' : 'Premium';
      toast.error(getText(
        `¡Guerrero! Necesitas el plan ${planNeeded} o superior para acceder.`,
        `¡Guerrera! Necesitas el plan ${planNeeded} o superior para acceder.`
      ));
      return;
    }
    navigate(path);
  };

  // ============= BLOQUEO TOTAL SI EL PLAN ESTÁ VENCIDO =============
  // Si el usuario tiene un plan que está completamente expirado, mostrar SOLO el overlay de renovación
  // IMPORTANTE: Verificar days_until_expiry < 0 para evitar falsos positivos
  const isPlanFullyExpired = profile && 
    userPlan && 
    userPlan !== 'free' && 
    userPlan !== 'admin' && 
    profile.is_fully_expired &&
    (typeof profile.days_until_expiry === 'number' && profile.days_until_expiry < 0);

  if (loading || !modulesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${theme.primaryLight}20, white, ${theme.primaryLight}20)` }}>
        <div className="text-2xl font-bold" style={{ color: theme.primary }}>Cargando...</div>
      </div>
    );
  }

  // Si el plan está completamente vencido, mostrar SOLO el overlay de pago
  if (isPlanFullyExpired) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
        <PlanExpiredOverlay
          graceDaysRemaining={0}
          isFullyExpired={true}
          planType={profile.subscription_plan || userPlan}
          renewalPrice={profile.renewal_price || profile.subscription_price}
          isVipPrice={profile.is_vip_price || false}
          vipSpecialPrice={profile.is_vip_price ? profile.renewal_price : null}
          vipPeriod={profile.vip_period || null}
          subscriptionPeriod={profile.subscription_period || 'mensual'}
          onRenew={() => navigate(`/checkout?renewal=1&plan=${encodeURIComponent(profile.subscription_plan || userPlan || 'basic')}&period=${encodeURIComponent(profile.subscription_period || 'monthly')}${profile.renewal_price ? `&amount=${profile.renewal_price}` : ''}`)}
          onPayOnline={() => navigate(`/checkout?renewal=1&plan=${encodeURIComponent(profile.subscription_plan || userPlan || 'basic')}&period=${encodeURIComponent(profile.subscription_period || 'monthly')}${profile.renewal_price ? `&amount=${profile.renewal_price}` : ''}`)}
        />
        
        {/* Modal de renovación/pago */}
        <VIPRenewalModal
          isOpen={showRenewalModal}
          onClose={() => setShowRenewalModal(false)}
          vipStatus={{
            ...vipStatus,
            // NO usar fallback 125000 aqui - dejar que el modal determine la moneda correcta
            fixed_price: vipStatus?.fixed_price || vipStatus?.special_price || vipStatus?.monthly_price || profile.renewal_price || profile.subscription_price || null,
            fixed_price_usd: vipStatus?.fixed_price_usd || profile.renewal_price_usd,
            // Precio fijo VIP SIN los fallbacks genéricos de arriba: solo lo que el
            // admin realmente fijó en vip_clients.fixed_price (use_fixed_price=true).
            // El modal de renovación lo usa para decidir si hay un override real por
            // periodo; los fallbacks genéricos (profile.subscription_price, etc.) NO
            // deben alimentar ese cálculo - ese era el bug del precio anual incorrecto.
            vip_raw_fixed_price: vipStatus?.fixed_price || null,
            vip_raw_fixed_price_usd: vipStatus?.fixed_price_usd || null,
            purchase_currency: vipStatus?.purchase_currency || profile.purchase_currency,
            plan_type: profile.subscription_plan || vipStatus?.plan_type || userPlan,
            email: profile?.email || user?.email,
            name: profile?.name || user?.name,
            subscription_period: vipStatus?.subscription_period || profile.subscription_period || 'monthly'
          }}
          onPaymentComplete={() => {
            setShowRenewalModal(false);
            toast.success('¡Pago procesado! Gracias por tu renovación.');
            // Refrescar la página para actualizar el estado del plan
            window.location.reload();
          }}
        />
      </div>
    );
  }

  // Usar colores del tema
  const accentColor = theme.primary;
  
  // Obtener módulos de tipo programa desde backend (o usar fallback)
  const programs = platformModules
    .filter(m => m.category === 'program' && m.is_active)
    .sort((a, b) => a.order - b.order)
    .map(m => ({
      id: m.id,
      title: m.name,
      description: getText(m.description, m.description),
      icon: ICON_MAP[m.icon] || FaFire,
      path: m.path,
      badge: m.badge,
      allowedPlans: m.allowed_plans || []
    }));

  // Obtener quick actions desde backend (o usar fallback)
  const quickActions = platformModules
    .filter(m => m.category === 'quick_action' && m.is_active)
    .sort((a, b) => a.order - b.order)
    .map(m => ({
      title: getText(m.name, m.name),
      icon: ICON_MAP[m.icon] || FaCalendar,
      path: m.path,
      allowedPlans: m.allowed_plans || []
    }));

  // FIT EVOLUTION: tarjetas unificadas — "Mi Plan de Alimentación" y "Mi Rutina" cuando ya hay plan,
  // o UNA SOLA tarjeta "Empezar mi Fit Evolution" cuando aún no ha llenado el formulario único.
  // Todo pasa por /personalized-form (formulario único con perfil metabólico + rutina + dieta).
  const feStatusLoaded = basicPlanStatus !== null;
  // Detectar envío reciente del formulario (hasta 3 min) — evita el "flash" al CTA de llenar.
  const feJustSubmitted = (() => {
    try {
      const ts = parseInt(sessionStorage.getItem('fe_just_submitted') || '0', 10);
      if (!ts) return false;
      const ageMs = Date.now() - ts;
      if (ageMs > 3 * 60 * 1000) { sessionStorage.removeItem('fe_just_submitted'); return false; }
      return true;
    } catch (_e) { return false; }
  })();
  const feDietReleased = basicPlanStatus?.diet?.is_released && !basicPlanStatus?.diet?.needs_continuation;
  const feWorkoutReleased = basicPlanStatus?.workout?.is_released && !basicPlanStatus?.workout?.needs_continuation;
  const feDietPending = basicPlanStatus?.diet?.is_pending || (isBasicUser && !feStatusLoaded && feJustSubmitted) || (isBasicUser && feJustSubmitted && !feDietReleased);
  const feWorkoutPending = basicPlanStatus?.workout?.is_pending || (isBasicUser && !feStatusLoaded && feJustSubmitted) || (isBasicUser && feJustSubmitted && !feWorkoutReleased);
  const feNeedsContinuation = basicPlanStatus?.diet?.needs_continuation || basicPlanStatus?.workout?.needs_continuation;
  const feHasStarted = feDietReleased || feWorkoutReleased || feDietPending || feWorkoutPending;
  // Mientras carga (usuarios basic sin datos aún), mostrar spinner en el card para
  // evitar mostrar el CTA por defecto "Contesta tu perfil" que envía a la clienta
  // de vuelta al formulario.
  const feLoading = isBasicUser && !feStatusLoaded && !feJustSubmitted;

  const getBasicWorkoutTitle = () => {
    if (feLoading) return { title: 'Mi Rutina de Entrenamiento', description: 'Cargando estado de tu plan…', path: '/dashboard' };
    if (feWorkoutReleased) return { title: 'Mi Rutina de Entrenamiento', description: 'Tu entrenamiento personalizado', path: '/my-workout' };
    if (feWorkoutPending) return { title: 'Mi Rutina de Entrenamiento', description: 'Tu plan está en preparación (24h)', path: '/fit-evolution/preparing' };
    if (feNeedsContinuation) return { title: 'Mi Rutina de Entrenamiento', description: 'Actualiza tu plan mensual', path: '/workout-generator?continuation=true' };
    return { title: 'Mi Rutina de Entrenamiento', description: 'Contesta tu perfil y recibe tu rutina', path: '/workout-generator' };
  };

  const getBasicDietTitle = () => {
    if (feLoading) return { title: 'Mi Plan de Alimentación', description: 'Cargando estado de tu plan…', path: '/dashboard' };
    if (feDietReleased) return { title: 'Mi Plan de Alimentación', description: '6 recetas por comida · Cocina Inteligente', path: '/fit-evolution/recipes' };
    if (feDietPending) return { title: 'Mi Plan de Alimentación', description: 'Tu plan está en preparación (24h)', path: '/fit-evolution/preparing' };
    if (feNeedsContinuation) return { title: 'Mi Plan de Alimentación', description: 'Actualiza tu plan mensual', path: '/macro-calculator?continuation=true' };
    return { title: 'Mi Plan de Alimentación', description: 'Contesta tu perfil y recibe tu dieta', path: '/macro-calculator' };
  };
  
  const basicWorkout = getBasicWorkoutTitle();
  const basicDiet = getBasicDietTitle();

  // Fallback si no hay módulos cargados - CON IMÁGENES DEL CONFIG Y SERVICE KEYS
  // IMPORTANTE: Incluir variaciones de nombres de planes (basic, básico, plan básico, etc.)
  const basicPlans = ['basic', 'básico', 'basico', 'plan básico', 'plan basico', 'plan basic'];
  const premiumPlans = ['premium', 'plan premium', 'alimentación premium', 'alimentacion premium'];
  const goldPlans = ['gold', 'gold vip', 'vip gold', 'plan gold', 'plan vip', 'vip'];
  const allPaidPlans = [...basicPlans, ...premiumPlans, ...goldPlans, 'competitivo', 'competitive', 'competencia', 'plan competitivo', 'plan competencia', 'lifetime', 'de por vida'];
  const allPlans = ['free', ...allPaidPlans];
  
  // Lista de planes premium y superiores (NO incluye basic)
  const premiumAndAbove = [...premiumPlans, ...goldPlans, 'competitivo', 'competitive', 'competencia', 'plan competencia', 'lifetime'];
  
  // Lista para Mi Progreso, Data Progresión e Historial - incluye basic
  const allPaidPlansWithProgress = [...basicPlans, ...premiumPlans, ...goldPlans, 'competitivo', 'competitive', 'competencia', 'plan competencia', 'lifetime'];

  // ═════ Selector de imagen por PLAN + GÉNERO (sin llamadas a IA) ═════
  // Mapeo de categorías (nombres literales de los planes):
  //   • AUTOMÁTICO  → Fit Evolution / Plan Básico (isBasicUser = true)
  //   • PERSONALIZADO → Plan Premium, Plan Competitivo, Plan Gold VIP
  // Estructura de site_config:
  //   image_automatic_<male|female>_<section>
  //   image_personalized_<male|female>_<section>
  //   sections = diet | workout | progress | videos
  //
  // Cadena de fallback (de más específico a más genérico):
  //   1. image_{category}_{gender}_{section}         (nueva estructura del admin)
  //   2. program_{section}_image_{gender}            (portadas por género del cambio previo)
  //   3. program_{section}_image                     (portada genérica clásica)
  //   4. imagen Unsplash embebida en la tarjeta      (último recurso)
  const userGender = (user?.gender || '').toString().toLowerCase();
  const genderSuffix =
    (userGender === 'male' || userGender === 'm') ? 'male' :
    (userGender === 'female' || userGender === 'f') ? 'female' : '';
  const planCategory = isBasicUser ? 'automatic' : 'personalized';

  const pickImageByGender = (baseKey, section) => {
    // 1) Plan + género (nueva estructura por CATEGORÍA)
    if (section && genderSuffix) {
      const catKey = `image_${planCategory}_${genderSuffix}_${section}`;
      if (siteConfig?.[catKey]) return getAbsoluteImageUrl(siteConfig[catKey]);
    }
    // 2) Solo género (fallback del cambio previo)
    if (genderSuffix) {
      const genderKey = `${baseKey}_${genderSuffix}`;
      if (siteConfig?.[genderKey]) return getAbsoluteImageUrl(siteConfig[genderKey]);
    }
    // 3) Portada genérica clásica
    return getAbsoluteImageUrl(siteConfig?.[baseKey]);
  };
  
  const fallbackPrograms = [
    // Para usuarios premium/gold/competitivo/vip - sus programas personalizados
    { id: 1, title: 'Mi Plan de Alimentación', description: 'Tu dieta personalizada', icon: FaUtensils, path: '/my-diet', allowedPlans: premiumAndAbove, serviceKey: 'my_diet', image: pickImageByGender('program_diet_image', 'diet') || 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop' },
    { id: 2, title: 'Mi Rutina', description: 'Tu entrenamiento personalizado', icon: FaDumbbell, path: '/my-workout', allowedPlans: premiumAndAbove, serviceKey: 'my_workout', image: pickImageByGender('program_workout_image', 'workout') || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop' },
    // Mi Progreso - INCLUYE plan básico
    { id: 3, title: 'Mi Progreso', description: 'Rastrea tu evolución', icon: FaChartLine, path: '/my-progress', allowedPlans: allPaidPlansWithProgress, serviceKey: 'data_progress', image: pickImageByGender('program_progress_image', 'progress') || 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop' },
    // Calendario Gold - SOLO para usuarios Gold (incluyendo gold vip)
    { id: 10, title: 'Calendario Gold', description: 'Reserva sesiones con Deisy', icon: FaCalendar, path: '/gold-calendar', allowedPlans: goldPlans, serviceKey: 'gold_calendar', image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=400&h=300&fit=crop' },
    // Fit Evolution (basic): SOLO se incluyen si el usuario ES basic. Los usuarios
    // premium/gold/competitivo ya tienen "Mi Plan de Alimentación" y "Mi Rutina"
    // (items 1 y 2) — evita duplicar tarjetas del mismo tema.
    ...(isBasicUser ? [
      { id: 4, title: basicDiet.title, description: basicDiet.description, icon: FaAppleAlt, path: basicDiet.path, allowedPlans: basicPlans, serviceKey: 'basic_diet', image: pickImageByGender('program_diet_image', 'diet') || getAbsoluteImageUrl(siteConfig?.program_menu_image) || 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop' },
      { id: 5, title: basicWorkout.title, description: basicWorkout.description, icon: FaDumbbell, path: basicWorkout.path, allowedPlans: basicPlans, serviceKey: 'basic_workout', image: pickImageByGender('program_workout_image', 'workout') || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop' },
    ] : []),
    // Recetas - SOLO para premium y superiores
    { id: 6, title: 'Recetas', description: 'Ideas de preparación', icon: FaUtensils, path: '/recipe-ideas', allowedPlans: premiumAndAbove, serviceKey: 'recipe_ideas', image: getAbsoluteImageUrl(siteConfig?.program_recipes_image) || 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop' },
    // Data Progresión - AHORA disponible para basic (Fit Evolution) también
    { id: 7, title: 'Data Progresión', description: 'Rastrea tus entrenamientos', icon: FaFire, path: '/data-progresion', allowedPlans: allPaidPlansWithProgress, serviceKey: 'data_progress_premium', image: getAbsoluteImageUrl(siteConfig?.program_data_image) || 'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=400&h=300&fit=crop' },
    { id: 17, title: 'Historial Progresión', description: 'Gráficas y récords personales', icon: FaTrophy, path: '/workout-history', allowedPlans: allPaidPlansWithProgress, serviceKey: 'workout_history', image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop' },
    // Videos - para todos los planes
    { id: 8, title: 'Videos', description: 'Biblioteca de entrenamiento', icon: FaVideo, path: '/videos-organized', allowedPlans: allPlans, serviceKey: 'videos', image: pickImageByGender('program_videos_image', 'videos') || 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=300&fit=crop' },
    // Ebooks - premium y superiores + Fit Evolution (basic)
    // 2026-02 · Fit Evolution (plan basic) también accede a la Biblioteca de
    // Ebooks (deisycfit.com/ebooks), igual que 1 a 1 / VIP Gold / Competitivo.
    { id: 9, title: 'Ebooks', description: 'Guías y recetarios', icon: FaBook, path: '/ebooks', allowedPlans: [...premiumAndAbove, ...basicPlans], serviceKey: 'ebooks', image: getAbsoluteImageUrl(siteConfig?.program_ebooks_image) || dynamicImages.ebooks || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=300&fit=crop' },
    { id: 11, title: 'Recetarios', description: 'Recetas en video', icon: FaUtensils, path: '/recetarios', allowedPlans: allPlans, serviceKey: 'recetarios', image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop' },
    { id: 12, title: 'Tienda', description: 'Productos y suplementos', icon: FaShoppingCart, path: '/tienda', allowedPlans: allPlans, serviceKey: 'tienda', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop' }
  ];

  // Filtrar programas: mostrar SOLO los que corresponden al plan del usuario
  // EXCEPCIÓN: Para usuarios básicos, mostrar también programas premium (bloqueados) para upsell
  const filterProgramsForUser = (programList) => {
    console.log('[Dashboard] Filtering programs for user plan:', userPlan);
    
    return programList.filter(program => {
      // Verificar si la sección está en construcción
      if (program.serviceKey && isSectionUnderConstruction(program.serviceKey)) {
        console.log(`[Dashboard] ${program.title}: hidden (under construction)`);
        return false;
      }
      
      // Admin siempre ve todo
      if (userRole === 'admin') {
        return true;
      }
      
      // PRIMERO: Verificar si el plan del usuario está en los allowedPlans del programa
      if (program.allowedPlans && program.allowedPlans.length > 0) {
        const normalizedUserPlan = (userPlan || 'free').toLowerCase().trim();

        // Comparación EXACTA (sin includes) para evitar falsos positivos
        // entre nombres parecidos (ej. "premium" no debe matchear "plan premium"
        // dentro de basicPlans, ni "basic" debe matchear "plan basic premium").
        const isInAllowedPlans = program.allowedPlans.some(allowed => {
          return normalizedUserPlan === (allowed || '').toLowerCase().trim();
        });

        // Bloqueo defensivo extra: las tarjetas de Fit Evolution (basic)
        // NUNCA se muestran a usuarios que NO son basic.
        if ((program.serviceKey === 'basic_diet' || program.serviceKey === 'basic_workout') && !isBasicUser) {
          console.log(`[Dashboard] ${program.title}: hidden (basic-only card, user plan=${normalizedUserPlan})`);
          return false;
        }

        // Petición explícita del usuario Fit Evolution: NO mostrar tarjetas bloqueadas.
        // "Solo quiero que se muestre lo que está disponible".
        if (!isInAllowedPlans) {
          console.log(`[Dashboard] ${program.title}: hidden (plan "${normalizedUserPlan}" not in allowedPlans: [${program.allowedPlans.join(', ')}])`);
          return false;
        }
      }
      
      console.log(`[Dashboard] ${program.title}: VISIBLE`);
      return true;
    });
  };

  // Usar los programas fallback con los permisos del plan
  const filteredPrograms = filterProgramsForUser(fallbackPrograms);
  
  // ORDENAR: Primero los accesibles, luego los bloqueados
  const displayPrograms = [...filteredPrograms].sort((a, b) => {
    const normalizedUserPlan = (userPlan || 'free').toLowerCase().trim();
    const matchExact = (allowed) => normalizedUserPlan === (allowed || '').toLowerCase().trim();

    // Verificar si programa A está bloqueado
    const isALocked = a.allowedPlans && a.allowedPlans.length > 0 && !a.allowedPlans.some(matchExact);

    // Verificar si programa B está bloqueado
    const isBLocked = b.allowedPlans && b.allowedPlans.length > 0 && !b.allowedPlans.some(matchExact);
    
    // Admin no tiene nada bloqueado
    if (userRole === 'admin') return 0;
    
    // Ordenar: desbloqueados primero (false < true)
    if (isALocked && !isBLocked) return 1;  // A bloqueado, B no -> B primero
    if (!isALocked && isBLocked) return -1; // A no bloqueado, B sí -> A primero
    return 0; // Mismo estado -> mantener orden original
  });

  // Colores del diseño Electric Obsidian - WOW Factor
  const NEON_PINK = 'var(--brand-primary)';
  const NEON_CYAN = '#00F0FF';
  const NEON_LIME = '#CCFF00';

  return (
    <div 
      className="min-h-screen relative" 
      style={{ 
        backgroundColor: '#0a0a0a',
        fontFamily: 'DM Sans, sans-serif'
      }}
    >
      {/* Mesh Gradient Background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.15), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.1), transparent 40%)'
        }}
      />
      
      {/* Banner de cuenta gratuita */}
      {!hasPaidPlan && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 py-3 px-6 text-center"
          style={{ background: 'linear-gradient(90deg, var(--brand-primary), #00F0FF)' }}
        >
          <p className="text-white text-xs font-bold tracking-wider uppercase">
            {getText('Guerrero', 'Guerrera')} · Cuenta gratuita · 
            <button 
              onClick={() => navigate('/plans')} 
              className="ml-2 underline"
            >
              Actualiza ahora
            </button>
          </p>
        </motion.div>
      )}

      {/* Header - Glassmorphism */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 
              className="text-2xl font-bold text-white tracking-tight uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              DeisyCFit
            </h1>
            <span 
              className="px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase rounded-full"
              style={{ 
                background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                color: 'white'
              }}
            >
              {(() => {
                const rawPlan = (profile?.subscription_plan || user?.subscription_plan || 'free').toLowerCase();
                const planDisplayMap = {
                  'competitivo': 'COMPETITIVO', 'gold': 'GOLD VIP', 'premium': 'PREMIUM',
                  'basic': 'FIT EVOLUTION', 'videos': 'VIDEOS', 'admin': 'ADMIN', 'free': 'FREE'
                };
                return planDisplayMap[rawPlan] || rawPlan.replace(/^plan[_-]?/i, '').toUpperCase() || 'FREE';
              })()}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Campana de notificaciones — visible para TODOS los usuarios.
                El componente elige internamente el endpoint (admin vs cliente). */}
            <NotificationBell />
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin-dashboard')}
                variant="ghost"
                className="text-gray-400 hover:text-[var(--brand-primary)] text-xs uppercase tracking-wider font-semibold"
                size="sm"
              >
                Admin
              </Button>
            )}
            <Button
              onClick={handleWhatsApp}
              className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full w-10 h-10 p-0 hover:shadow-[0_0_20px_rgba(37,211,102,0.5)] transition-all"
              size="sm"
            >
              <FaWhatsapp className="text-lg" />
            </Button>
            <Button
              onClick={() => setShowProfileModal(true)}
              variant="ghost"
              className="text-gray-400 hover:text-[#00F0FF] rounded-full w-10 h-10 p-0"
              size="sm"
              title="Mi Perfil"
            >
              <FaCog />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-500 hover:text-white rounded-full w-10 h-10 p-0"
              size="sm"
            >
              <FaSignOutAlt />
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Hero Welcome - Electric Style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <p 
            className="text-xs font-bold uppercase tracking-[0.3em] mb-4"
            style={{ color: 'var(--brand-primary)' }}
          >
            Tu espacio fitness
          </p>
          <h1 
            className="text-5xl md:text-7xl font-bold text-white mb-6 uppercase tracking-tight leading-none"
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="greeting-title"
          >
            {getText('Hola,', 'Hola,')}
            <br />
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              {getText('Guerrero', 'Guerrera')}
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl">
            Transforma tu cuerpo, transforma tu vida. Cada día cuenta.
          </p>
        </motion.div>

        {/* Modal overlay de anuncio: REMOVIDO. La invitación ahora está solo en el banner inline
            (HormonalCycleBanner) para no bloquear la pantalla. */}

        {/* Modal overlay de alerta de cambio de fase: REMOVIDO también. El card inline
            HormonalCyclePhaseModal (abajo) ya muestra esa información sin bloquear el dashboard. */}

        {/* Card inline (siempre visible) de la fase actual del ciclo */}
        {/* Ocultar Mi Ciclo Hormonal para users basic (Fit Evolution) — es feature Premium/VIP */}
        {!isBasicUser && (
          <div className="mb-3">
            <HormonalCyclePhaseModal />
          </div>
        )}

        {/* Banner Mi Ciclo Hormonal — solo aparece la primera vez (banner_status==='unseen') */}
        <div className="mb-6">
          {/* 🎬 Onboarding de bienvenida: modal auto + banner con 4 pasos.
              Desaparece cuando la clienta envía el formulario Fit Evolution. */}
          <WelcomeOnboarding
            token={localStorage.getItem('deisycfit_token')}
            dietFormPath={(isBasicUser || !hasPaidPlan) ? '/macro-calculator' : '/personalized-form'}
          />

          {!isBasicUser && <HormonalCycleBanner />}

          {/* Banner destacado: mensaje del coach en fotos de progreso.
              Sólo se muestra cuando la usuaria ya cambió su contraseña
              (must_change_password = false y modal cerrado). */}
          <CoachProgressCommentBanner
            user={user}
            canShow={!user?.must_change_password && !showChangePasswordModal}
          />
          {/* Fit Evolution: banner de actualización cada 4 semanas (solo plan basic) */}
          <FitEvolutionUpdateBanner user={user} />
          {/* Badge visible durante las 24h post envío del formulario de progreso */}
          <FitEvolutionPendingReleaseBadge user={user} />
          {/* 2026-02: Banner de comunidad (WhatsApp/Telegram) arriba del widget del programa */}
          <FitEvolutionCommunityBanner user={user} />
          {/* Fit Evolution: widget de progreso 12 semanas (solo plan basic) */}
          <FitEvolutionWorkoutProgressWidget user={user} />
        </div>

        {/* Card permanente para acceder a Mi Ciclo Hormonal — oculto para basic */}
        {!isBasicUser && (
          <div className="mb-6">
            <HormonalCycleCard />
          </div>
        )}

        {/* Tarjeta VIP - Glassmorphism Premium */}
        {vipStatus?.is_vip && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-10"
          >
            <div 
              className="relative overflow-hidden rounded-3xl p-8 backdrop-blur-xl"
              style={{ 
                background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(0, 240, 255, 0.1))',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 0 40px rgba(var(--brand-primary-glow), 0.1)'
              }}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Tu Membresía</p>
                  <h3 
                    className="text-3xl md:text-4xl font-bold text-white mb-2 uppercase"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {vipStatus.name || profile?.name || 'Miembro'}
                  </h3>
                  {/* Tipo de Plan Visible */}
                  <div className="mb-3">
                    {(() => {
                      const pt = (vipStatus.plan_type || '').toString().toLowerCase()
                        .replace(/^(vip-renewal-|vip_renewal_|renewal-|renewal_|renovación |renovacion |plan-|plan_|plan )/i, '')
                        .replace(/^básico$/i, 'basic').replace(/^basico$/i, 'basic')
                        .replace(/^competitiva$/i, 'competitivo');
                      const styleMap = {
                        'competitivo': 'bg-orange-500/20 text-orange-400 border border-orange-500/50',
                        'gold': 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50',
                        'premium': 'bg-purple-500/20 text-purple-400 border border-purple-500/50',
                        'basic': 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                      };
                      const nameMap = {
                        'competitivo': 'Plan Competitivo',
                        'gold': 'Plan Gold VIP',
                        'premium': 'Plan Premium',
                        'basic': 'Fit Evolution',
                        'videos': 'Videos'
                      };
                      return (
                        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider ${styleMap[pt] || 'bg-gray-500/20 text-gray-400 border border-gray-500/50'}`}
                              data-testid="vip-plan-type-badge">
                          {nameMap[pt] || `Plan ${pt.charAt(0).toUpperCase() + pt.slice(1) || 'VIP'}`}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-2">
                      <FaClock style={{ color: '#00F0FF' }} />
                      {vipStatus.days_remaining != null ? vipStatus.days_remaining : '—'} días restantes
                    </span>
                    <span className="flex items-center gap-2">
                      <FaCheckCircle style={{ color: vipStatus.is_expired ? '#F59E0B' : 'var(--brand-primary)' }} />
                      {vipStatus.is_expired ? 'Membresía Vencida' : 'Membresía Activa'}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col items-start md:items-end gap-4">
                  <div className="text-left md:text-right">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Próximo cobro</p>
                    {(() => {
                      // Mostrar precio en la moneda correcta - prioridad: moneda de compra del cliente
                      const priceCOP = vipStatus.fixed_price || vipStatus.special_price || vipStatus.monthly_price;
                      const priceUSD = vipStatus.fixed_price_usd;
                      const purchaseCurr = vipStatus.purchase_currency;
                      
                      // Si compró en USD y tiene precio USD, mostrar USD
                      if ((purchaseCurr === 'USD' && priceUSD && priceUSD > 0) || (!priceCOP && priceUSD && priceUSD > 0)) {
                        return (
                          <p className="text-2xl font-bold text-white" data-testid="next-payment-price">
                            ${priceUSD.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} 
                            <span className="text-sm text-gray-400 ml-1">USD</span>
                          </p>
                        );
                      }
                      if (priceCOP && priceCOP > 0) {
                        return (
                          <p className="text-2xl font-bold text-white" data-testid="next-payment-price">
                            ${priceCOP.toLocaleString('es-CO')} 
                            <span className="text-sm text-gray-400 ml-1">COP</span>
                          </p>
                        );
                      }
                      return (
                        <p className="text-lg text-gray-400">
                          Consultar valor
                        </p>
                      );
                    })()}
                    {vipStatus.subscription_period && (
                      <p className="text-xs text-gray-500 mt-1">
                        Período: {vipStatus.subscription_period}
                      </p>
                    )}
                    {vipStatus.end_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Vence: {new Date(vipStatus.end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  
                  {/* Botones de Renovación VIP - Solo mostrar cuando faltan 7 días o menos, o ya expiró */}
                  {(vipStatus.is_expired || (vipStatus.days_remaining != null && vipStatus.days_remaining <= 7)) && (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Renovar con:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => setShowRenewalModal(true)}
                        size="sm"
                        className="rounded-full px-4 font-semibold text-white transition-all hover:scale-105"
                        style={{ 
                          background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                          boxShadow: '0 0 15px rgba(var(--brand-primary-glow), 0.3)'
                        }}
                      >
                        <FaCreditCard className="mr-2 text-sm" /> Pagar Online
                      </Button>
                      <Button
                        onClick={handleWhatsApp}
                        size="sm"
                        variant="outline"
                        className="rounded-full px-4 font-semibold transition-all hover:scale-105"
                        style={{ 
                          borderColor: '#25D366',
                          color: '#25D366'
                        }}
                      >
                        <FaWhatsapp className="mr-2" /> WhatsApp
                      </Button>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tarjeta de Suscripción para planes Premium/Gold/Competitivo - NO VIP */}
        {hasPaidPlan && !vipStatus?.is_vip && (profile?.subscription_plan || user?.subscription_plan) && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-10"
          >
            {/* Verificar si la membresía está expirada */}
            {(() => {
              const endDate = profile?.subscription_end_date ? new Date(profile.subscription_end_date) : null;
              const isExpired = endDate && endDate < new Date();
              
              if (isExpired) {
                return (
                  <div 
                    className="relative overflow-hidden rounded-3xl p-6 md:p-8 backdrop-blur-xl"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(var(--brand-primary-glow), 0.1))',
                      border: '2px solid rgba(245, 158, 11, 0.4)',
                      boxShadow: '0 0 30px rgba(245, 158, 11, 0.2)'
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-3xl">⏰</span>
                          <h3 className="text-xl md:text-2xl font-bold text-amber-400">
                            Tu membresía ha expirado
                          </h3>
                        </div>
                        <p className="text-gray-300 mb-2">
                          ¡Nos encantaría que continuaras con tu proceso de transformación! 💪
                        </p>
                        <p className="text-sm text-gray-400">
                          Plan anterior: <span className="text-white font-semibold">{(profile?.subscription_plan || user?.subscription_plan)?.toUpperCase()}</span>
                          {profile?.subscription_period && (
                            <span className="ml-2">• {profile.subscription_period.charAt(0).toUpperCase() + profile.subscription_period.slice(1)}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Venció: {endDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      
                      <div className="flex flex-col gap-3">
                        <Button
                          onClick={() => setShowRenewalModal(true)}
                          size="lg"
                          className="rounded-full px-6 font-bold text-white transition-all hover:scale-105"
                          style={{ 
                            background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                            boxShadow: '0 0 20px rgba(var(--brand-primary-glow), 0.4)'
                          }}
                        >
                          <FaCreditCard className="mr-2" /> Renovar Ahora
                        </Button>
                        <Button
                          onClick={handleWhatsApp}
                          size="sm"
                          variant="outline"
                          className="rounded-full px-4 font-semibold transition-all hover:scale-105"
                          style={{ borderColor: '#25D366', color: '#25D366' }}
                        >
                          <FaWhatsapp className="mr-2" /> Consultar por WhatsApp
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <div 
                  className="relative overflow-hidden rounded-3xl p-6 md:p-8 backdrop-blur-xl"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(204, 255, 0, 0.1))',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 0 30px rgba(0, 240, 255, 0.1)'
                  }}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    {/* Info del Plan */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Tu Membresía</p>
                      <h3 
                        className="text-2xl md:text-3xl font-bold text-white mb-2 uppercase"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {(() => {
                      // Normalización defensiva: si viene "renewal_premium", "vip-renewal-gold",
                      // "Renovación Premium", "plan-basic"…, se limpia y matchea igual.
                      const rawPlan = (profile?.subscription_plan || user?.subscription_plan || 'Activo').toString().toLowerCase();
                      const cleanPlan = rawPlan
                        .replace(/^(vip-renewal-|vip_renewal_|renewal-|renewal_|renovación |renovacion |plan-|plan_|plan )/i, '')
                        .replace(/^básico$/i, 'basic').replace(/^basico$/i, 'basic')
                        .replace(/^competitiva$/i, 'competitivo');
                      const planDisplayMap = {
                        'competitivo': 'Plan Competitivo', 'gold': 'Plan Gold VIP', 'premium': 'Plan Premium',
                        'basic': 'Fit Evolution', 'videos': 'Videos', 'admin': 'Admin'
                      };
                      return planDisplayMap[cleanPlan] || `Plan ${cleanPlan.charAt(0).toUpperCase() + cleanPlan.slice(1)}`;
                    })()}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        {/* Tipo de plan (mensual, bimestral, etc.) */}
                        {(profile?.subscription_period || planInfo?.subscription_period) && (
                          <span className="flex items-center gap-2">
                            <FaClock style={{ color: 'var(--brand-primary)' }} />
                            {(profile?.subscription_period || planInfo?.subscription_period)?.charAt(0).toUpperCase() + (profile?.subscription_period || planInfo?.subscription_period)?.slice(1)}
                          </span>
                        )}
                        {/* Fecha de inicio */}
                        {profile?.subscription_start_date && (
                          <span className="flex items-center gap-2">
                            <FaCalendar style={{ color: '#00F0FF' }} />
                            Inicio: {new Date(profile.subscription_start_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        <span className="flex items-center gap-2">
                          <FaCheckCircle style={{ color: '#CCFF00' }} />
                          Activo
                        </span>
                      </div>
                    </div>
                    
                    {/* Próximo Cobro y Renovar */}
                    <div className="flex flex-col items-start md:items-end gap-4">
                      <div className="text-left md:text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Próximo cobro</p>
                        {profile?.subscription_price || planInfo?.price_cop ? (
                          <p className="text-2xl font-bold" style={{ color: '#00F0FF' }} data-testid="non-vip-next-payment-price">
                            ${(planInfo?.price_cop || profile?.subscription_price)?.toLocaleString('es-CO')} COP
                          </p>
                        ) : (
                          <p className="text-lg font-bold text-gray-400">
                            Consultar valor
                          </p>
                        )}
                        {/* Fecha del próximo cobro */}
                        {profile?.subscription_end_date && (
                          <p className="text-xs text-gray-500 mt-1">
                            Próximo: {new Date(profile.subscription_end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                  
                  {/* Botones de Renovación - Solo mostrar cuando quedan 7 días o menos */}
                  {profile?.days_until_expiry != null && profile.days_until_expiry <= 7 && (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Renovar con:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => setShowRenewalModal(true)}
                        size="sm"
                        className="rounded-full px-4 font-semibold text-white transition-all hover:scale-105"
                        style={{ 
                          background: 'linear-gradient(135deg, #00F0FF, #CCFF00)',
                          boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)'
                        }}
                      >
                        <FaCreditCard className="mr-2 text-sm" /> Pagar Online
                      </Button>
                      <Button
                        onClick={handleWhatsApp}
                        size="sm"
                        variant="outline"
                        className="rounded-full px-4 font-semibold transition-all hover:scale-105"
                        style={{ 
                          borderColor: '#25D366',
                          color: '#25D366'
                        }}
                      >
                        <FaWhatsapp className="mr-2" /> WhatsApp
                      </Button>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>
              );
            })()}
          </motion.div>
        )}

        {/* TARJETA: Completar Formulario - Neon Glow */}
        {/* Solo mostrar si el endpoint confirmó que NO tiene plan (form_completed === false explícito) */}
        {needsPersonalizedPlan && 
         !personalizedPlanLoading &&
         personalizedPlanStatus !== null &&
         personalizedPlanStatus.form_completed === false && 
         personalizedPlanStatus.status !== 'sent' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-12"
          >
            <div 
              className="relative overflow-hidden rounded-3xl p-8 group cursor-pointer hover:scale-[1.01] transition-all duration-300"
              style={{ 
                background: 'linear-gradient(135deg, var(--brand-primary), #D41C70)',
                boxShadow: '0 0 40px rgba(var(--brand-primary-glow), 0.3)'
              }}
              onClick={() => navigate('/personalized-form')}
            >
              {/* Animated glow */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ boxShadow: 'inset 0 0 60px rgba(255, 255, 255, 0.1)' }}
              />
              
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <FaClipboardList className="text-3xl text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl font-bold text-white mb-1 uppercase"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Completa tu Formulario
                    </h3>
                    <p className="text-white/80">
                      Necesitamos conocerte para crear tu plan personalizado
                    </p>
                  </div>
                </div>
                <Button
                  className="text-white font-bold px-8 py-3 rounded-full uppercase tracking-wider"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                  data-testid="complete-form-btn"
                >
                  Completar →
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* BANNER: Actualización de formulario disponible */}
        {formUpdateStatus?.has_pending_update && formUpdateStatus?.total_new_questions > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div 
              className="relative overflow-hidden rounded-2xl p-6 cursor-pointer hover:scale-[1.01] transition-all duration-300"
              style={{ 
                background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)'
              }}
              onClick={() => navigate('/personalized-form?mode=update')}
              data-testid="form-update-banner"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <FaBell className="text-2xl text-white animate-bounce" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-0.5">
                      ¡Hay {formUpdateStatus.total_new_questions} preguntas nuevas!
                    </h3>
                    <p className="text-white/80 text-sm">
                      Actualiza tu formulario para que podamos mejorar tu plan personalizado
                    </p>
                  </div>
                </div>
                <Button
                  className="text-white font-semibold px-6 py-2 rounded-full whitespace-nowrap"
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}
                  data-testid="update-form-btn"
                >
                  Actualizar →
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* TARJETA: Plan pendiente de asignación */}
        {/* Solo mostrar si completó el formulario Y el plan está en preparación */}
        {needsPersonalizedPlan && personalizedPlanStatus?.form_completed === true && 
         personalizedPlanStatus?.status === 'pending' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-10"
          >
            <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white overflow-hidden border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <FaClock className="text-3xl text-white animate-pulse" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Tu Plan Está en Preparación</h3>
                    <p className="text-white/90">
                      Deisy está preparando tu plan personalizado. Te notificaremos cuando esté listo.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* TARJETA: Plan listo para ver - NO mostrar esto, los usuarios van directo a sus tarjetas de Mi Plan/Mi Rutina */}
        {/* Esta tarjeta se elimina porque es redundante - los usuarios premium ya tienen las tarjetas de acceso */}

        {/* Sección de Actualización Mensual - Solo para usuarios con plan premium que ya tienen plan enviado */}
        {needsPersonalizedPlan && personalizedPlanStatus?.status === 'sent' && profile?.email && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <MonthlyUpdateSection userEmail={profile.email} />
          </motion.div>
        )}

        {/* 2026-02 · Composición Corporal y Proyecciones (Premium/Competitivo/VIP GOL) */}
        {needsPersonalizedPlan && bodyComposition && (() => {
          const bc = bodyComposition;
          const daysToTarget = bc.weeks_to_target != null ? bc.weeks_to_target * 7 : null;
          let dateLabel = bc.target_date_label || null;
          if (!dateLabel && daysToTarget != null && daysToTarget > 0) {
            const d = new Date();
            d.setDate(d.getDate() + daysToTarget);
            dateLabel = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
          }
          const cells = [
            bc.body_fat_pct != null && { label: '% Grasa Actual', value: `${bc.body_fat_pct}%`, color: '#34D399' },
            bc.body_fat_pct_target != null && { label: '% Grasa Objetivo', value: `${bc.body_fat_pct_target}%`, color: '#00F0FF' },
            bc.weekly_fat_loss_kg > 0 && { label: 'Pérdida Grasa/Semana', value: `${bc.weekly_fat_loss_kg} kg`, color: '#FF6B9D' },
            daysToTarget != null && { label: 'Días al Objetivo', value: daysToTarget, subtitle: dateLabel ? `hasta ${dateLabel}` : null, color: '#FBBF24' }
          ].filter(Boolean);
          if (cells.length === 0) return null;
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-10"
              data-testid="dashboard-body-composition"
            >
              <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="text-lg font-bold text-white mb-4 uppercase flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  📊 Composición Corporal y Proyecciones
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {cells.map((c, i) => (
                    <div key={i} className="text-center" data-testid={`dash-body-comp-${i}`}>
                      <p className="text-2xl md:text-3xl font-bold" style={{ color: c.color }}>{c.value}</p>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{c.label}</p>
                      {c.subtitle && (
                        <p className="text-[11px] text-gray-400 mt-0.5">{c.subtitle}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Formularios Clínicos Pendientes — oculto para users basic (feature Premium/VIP) */}
        {profile?.email && !isBasicUser && (
          <ClinicalFormWidget userEmail={profile.email} />
        )}

        {/* Widget de recetas favoritas — visible cuando el user es Fit Evolution y tiene favoritas */}
        {profile?.email && isBasicUser && feDietReleased && (
          <FavoriteRecipesWidget user={{ email: profile.email }} />
        )}

        {/* Programs Section - Bento Grid with Glow */}
        <div className="mb-16">
          <div className="flex justify-between items-end mb-10">
            <div>
              <p 
                className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
                style={{ color: '#00F0FF' }}
              >
                Tus programas
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Explora
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {displayPrograms.map((program, index) => {
              const isLocked = !hasAccess(program.allowedPlans);
              const minPlan = program.allowedPlans.includes('basic') ? 'FIT EVOLUTION' : 'PREMIUM';
              const isLarge = index === 0 || index === 3;
              
              return (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + index * 0.05 }}
                  className={isLarge ? 'col-span-2 row-span-2' : ''}
                >
                  <div 
                    className={`relative overflow-hidden rounded-2xl cursor-pointer group transition-all duration-500 h-full
                      ${isLocked ? 'opacity-40' : 'hover:-translate-y-2'}
                      ${isLarge ? 'min-h-[400px]' : 'min-h-[200px]'}`}
                    style={{ 
                      background: '#111111',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                    onClick={() => handleFeatureClick(program.path, program.allowedPlans)}
                  >
                    {/* Background Image with Skeleton Loading */}
                    {program.image && (
                      <div className="absolute inset-0">
                        {/* Skeleton Loader - muestra mientras la imagen carga */}
                        {!loadedImages[program.id] && (
                          <div className="absolute inset-0 animate-pulse">
                            <div 
                              className="w-full h-full"
                              style={{ 
                                background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1) 0%, rgba(0, 240, 255, 0.1) 50%, rgba(var(--brand-primary-glow), 0.1) 100%)',
                                backgroundSize: '200% 200%',
                                animation: 'shimmer 2s ease-in-out infinite'
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <program.icon className="text-4xl text-white/20 animate-pulse" />
                            </div>
                          </div>
                        )}
                        <img 
                          src={program.image} 
                          alt={program.title}
                          loading="lazy"
                          onLoad={() => setLoadedImages(prev => ({ ...prev, [program.id]: true }))}
                          onError={(e) => { 
                            e.target.style.display = 'none';
                            setLoadedImages(prev => ({ ...prev, [program.id]: true }));
                          }}
                          className={`w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-110 transition-all duration-700 ${
                            !loadedImages[program.id] ? 'opacity-0' : ''
                          }`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="relative z-10 p-6 h-full flex flex-col justify-end">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 backdrop-blur-sm transition-all group-hover:scale-110"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.3), rgba(0, 240, 255, 0.3))',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <program.icon className="text-xl text-white" />
                      </div>
                      <h3 
                        className={`text-white font-bold mb-2 uppercase tracking-tight ${isLarge ? 'text-2xl md:text-3xl' : 'text-lg'}`}
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {program.title}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {program.description}
                      </p>
                    </div>
                    
                    {/* Hover Glow Border */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none rounded-2xl"
                      style={{ 
                        boxShadow: 'inset 0 0 0 2px var(--brand-primary), 0 0 30px rgba(var(--brand-primary-glow), 0.3)'
                      }}
                    />
                    
                    {/* Lock overlay - Enhanced for upsell - MUESTRA NOMBRE DEL SERVICIO */}
                    {isLocked && (
                      <div 
                        className="absolute inset-0 flex flex-col items-center justify-end rounded-2xl z-20 pb-6"
                        style={{ background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 100%)' }}
                      >
                        {/* Título del servicio visible */}
                        <h3 
                          className={`text-white font-bold mb-3 uppercase tracking-tight text-center px-4 ${isLarge ? 'text-xl md:text-2xl' : 'text-base'}`}
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {program.title}
                        </h3>
                        
                        {/* Candado y plan requerido */}
                        <div className="text-center px-4">
                          <div 
                            className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                            style={{ 
                              background: 'linear-gradient(135deg, var(--brand-primary), #D41C70)',
                              boxShadow: '0 0 15px rgba(var(--brand-primary-glow), 0.4)'
                            }}
                          >
                            <FaCrown className="text-lg text-white" />
                          </div>
                          <span 
                            className="text-xs font-bold uppercase tracking-wider block"
                            style={{ color: 'var(--brand-primary)' }}
                          >
                            🔒 Plan {minPlan}+
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Section - Solo para usuarios sin plan premium/gold/competitivo */}
        {!needsPersonalizedPlan && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="rounded-3xl p-12 text-center text-white shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})` }}
          >
            <h2 className="text-4xl font-bold mb-4">{getText('¿Listo para más?', '¿Lista para más?')}</h2>
            <p className="text-xl mb-8 opacity-90">
              Desbloquea contenido exclusivo, planes personalizados y soporte VIP
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                onClick={() => navigate('/#planes')}
                className="text-white font-bold px-8 py-6 text-lg rounded-full"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              >
                <FaCrown className="mr-2" /> Ver Planes Premium
              </Button>
              <Button 
                onClick={handleWhatsApp}
                className="font-bold px-8 py-6 text-lg rounded-full"
                style={{ 
                  background: 'transparent', 
                  border: '2px solid var(--brand-primary)',
                  color: 'var(--brand-primary)'
                }}
              >
                <FaWhatsapp className="mr-2" /> Hablar con Deisy
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* ChatBot Widget */}
      {/* ChatWidget removido: las clientas ya tienen el botón de WhatsApp y el
          botón de correo. El chat flotante rosa creaba ruido innecesario. */}
      
      {/* Modales VIP */}
      <VIPPlanExpiredModal
        isOpen={showVIPExpiredModal}
        onClose={() => setShowVIPExpiredModal(false)}
        userName={user?.name || profile?.name}
        daysOverdue={vipStatus?.days_remaining ? Math.abs(vipStatus.days_remaining) : 0}
        onViewPlans={() => {
          setShowVIPExpiredModal(false);
          // Abrir directamente el modal de pago en lugar de navegar
          setShowRenewalModal(true);
        }}
      />
      
      <VIPSpecialPriceExpiredModal
        isOpen={showSpecialPriceExpiredModal}
        onClose={() => setShowSpecialPriceExpiredModal(false)}
        userName={user?.name || profile?.name}
        onViewPlans={() => {
          setShowSpecialPriceExpiredModal(false);
          // Abrir directamente el modal de pago en lugar de navegar
          setShowRenewalModal(true);
        }}
      />
      
      {/* Modal de cambio de contraseña para usuarios nuevos y VIPs */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={() => {
          toast.success('¡Tu contraseña ha sido configurada! Ya puedes usar tu cuenta.');
        }}
        isRequired={vipStatus?.needs_password_change || user?.must_change_password}
      />

      {/* Modal bloqueante de pago pendiente: ahora se maneja globalmente en App.js
          vía GlobalPaymentGate para que cubra TODAS las rutas (incluyendo
          /personalized-form). Se mantiene aquí comentado intencionalmente. */}

      {/* Modal de renovación/pago */}
      <VIPRenewalModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        vipStatus={{
          ...vipStatus,
          // NO usar fallback 125000 - dejar que el modal determine la moneda correcta
          fixed_price: vipStatus?.fixed_price || vipStatus?.special_price || vipStatus?.monthly_price || planInfo?.price_cop || planInfo?.price_monthly_cop || null,
          fixed_price_usd: vipStatus?.fixed_price_usd || profile?.renewal_price_usd,
          // Precio fijo VIP SIN los fallbacks genéricos de arriba (ver comentario en
          // el otro <VIPRenewalModal /> de este archivo).
          vip_raw_fixed_price: vipStatus?.fixed_price || null,
          vip_raw_fixed_price_usd: vipStatus?.fixed_price_usd || null,
          purchase_currency: vipStatus?.purchase_currency || profile?.purchase_currency,
          plan_type: profile?.subscription_plan || vipStatus?.plan_type || userPlan,
          email: user?.email,
          name: user?.name || profile?.name,
          subscription_period: vipStatus?.subscription_period || profile?.subscription_period || 'monthly'
        }}
        onPaymentComplete={() => {
          setShowRenewalModal(false);
          toast.success('¡Pago procesado! Gracias por tu renovación.');
          // CRÍTICO: Recargar la página para obtener datos actualizados del backend
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }}
      />

      {/* Overlay de Plan Expirado / Período de Gracia - PARA TODOS LOS USUARIOS */}
      {/* IMPORTANTE: Solo mostrar si days_until_expiry es estrictamente negativo (plan vencido) */}
      {profile && userPlan && userPlan !== 'free' && userPlan !== 'admin' && 
       (profile.is_in_grace_period || profile.is_fully_expired) && 
       (typeof profile.days_until_expiry === 'number' && profile.days_until_expiry < 0) && (
        <PlanExpiredOverlay
          graceDaysRemaining={profile.grace_days_remaining || 0}
          isFullyExpired={profile.is_fully_expired || false}
          planType={profile.subscription_plan || userPlan}
          renewalPrice={profile.renewal_price}
          isVipPrice={profile.is_vip_price || false}
          vipSpecialPrice={profile.is_vip_price ? profile.renewal_price : null}
          vipPeriod={profile.vip_period || null}
          subscriptionPeriod={profile.subscription_period || 'mensual'}
          onRenew={() => navigate(`/checkout?renewal=1&plan=${encodeURIComponent(profile.subscription_plan || userPlan || 'basic')}&period=${encodeURIComponent(profile.subscription_period || 'monthly')}${profile.renewal_price ? `&amount=${profile.renewal_price}` : ''}`)}
          onPayOnline={() => navigate(`/checkout?renewal=1&plan=${encodeURIComponent(profile.subscription_plan || userPlan || 'basic')}&period=${encodeURIComponent(profile.subscription_period || 'monthly')}${profile.renewal_price ? `&amount=${profile.renewal_price}` : ''}`)}
        />
      )}

      {/* Modal de Seguimiento de Patologías */}
      {showFollowupModal && (
        <FollowupModal onClose={() => {
          setShowFollowupModal(false);
          sessionStorage.setItem('followup_dismissed', 'true');
        }} />
      )}

      {/* Badge: Mensaje del día de la coach */}
      <CoachMessageBadge />

      {/* Inbox flotante: Mensajes guardados por la clienta */}
      <CoachMessagesInbox />

      {/* Modal de Perfil - Selección de Género */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl p-8 max-w-md w-full"
            style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <h2 className="text-2xl font-bold text-white mb-2">⚙️ Mi Perfil</h2>
            <p className="text-gray-400 text-sm mb-6">Configura tu perfil para una experiencia personalizada</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">¿Cuál es tu género?</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setProfileGender('female')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      profileGender === 'female'
                        ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10'
                        : 'border-gray-700 hover:border-[var(--brand-primary)]/50'
                    }`}
                  >
                    <div className="text-4xl mb-2">💪</div>
                    <div className={`font-bold ${profileGender === 'female' ? 'text-[var(--brand-primary)]' : 'text-white'}`}>
                      Guerrera
                    </div>
                    <div className="text-xs text-gray-500">Femenino</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfileGender('male')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      profileGender === 'male'
                        ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                        : 'border-gray-700 hover:border-[#00F0FF]/50'
                    }`}
                  >
                    <div className="text-4xl mb-2">🔥</div>
                    <div className={`font-bold ${profileGender === 'male' ? 'text-[#00F0FF]' : 'text-white'}`}>
                      Guerrero
                    </div>
                    <div className="text-xs text-gray-500">Masculino</div>
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowProfileModal(false)}
                  variant="outline"
                  className="flex-1 border-gray-600"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile || !profileGender}
                  className="flex-1 bg-gradient-to-r from-[var(--brand-primary)] to-[#FF69B4]"
                >
                  {savingProfile ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Modal de datos faltantes - aparece automáticamente para usuarios afectados */}
      {profile?.email && needsPersonalizedPlan && (
        <MissingDataModal 
          userEmail={profile.email} 
          onComplete={() => {
            toast.success('¡Gracias por actualizar tus datos!');
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;